import './CreativeAllocationChart.scss';
import cn from 'classnames';
import { Cell, Pie, PieChart } from 'recharts';
import { Form } from 'react-bootstrap';
import { Img } from '@Components/Img';
import { DisabledReasonWrapper } from '@Components/CreativeAllocation';
import { getCreativeThumbnail } from '@Components/MetricsTable/metrics-table-utils';
import { getChannelSeriesColor, Neutral500 } from '../../../utils/colors';

// Gray segment representing the still-unallocated remainder of the pie.
const UNALLOCATED_COLOR = Neutral500;

interface CreativeAllocationItem {
  isci: string;
  name: string;
  length: number;
  value: number;
  disabled?: boolean;
  disabledReason?: string;
}

interface CreativeAllocationChartProps {
  company: string;
  data: CreativeAllocationItem[];
  onChange: (isci: string, value: number) => void;
}

export const CreativeAllocationChart = ({
  company,
  data,
  onChange
}: CreativeAllocationChartProps) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);

  const coloredData = data.map((item, i) => ({
    ...item,
    color: getChannelSeriesColor(i, data.length <= 8)
  }));

  // The pie shows only allocated creatives plus a gray "Unallocated" remainder,
  // so it starts as a full gray ring and fills with color as values are entered.
  const unallocated = Math.max(0, 100 - total);
  const pieData = [
    ...coloredData.filter(item => item.value > 0),
    ...(unallocated > 0
      ? [{ isci: '__unallocated__', value: unallocated, color: UNALLOCATED_COLOR }]
      : [])
  ];

  return (
    <div className='creative-allocation-chart'>
      <div className='pie-area'>
        <PieChart width={180} height={180}>
          <Pie
            data={pieData}
            dataKey='value'
            nameKey='isci'
            cx='50%'
            cy='50%'
            innerRadius={50}
            outerRadius={85}
            startAngle={90}
            endAngle={-270}
            stroke='white'
            strokeWidth={2}
          >
            {pieData.map(entry => (
              <Cell key={entry.isci} fill={entry.color} />
            ))}
          </Pie>
        </PieChart>
        <div className={cn('pie-center-label', { valid: total === 100, invalid: total !== 100 })}>
          {total}%
        </div>
      </div>
      <div className='items-area'>
        {coloredData.map(item => (
          <CreativeAllocationCard
            key={item.isci}
            company={company}
            color={item.color}
            isci={item.isci}
            name={item.name}
            length={item.length}
            value={item.value}
            disabled={item.disabled}
            disabledReason={item.disabledReason}
            onChange={onChange}
          />
        ))}
      </div>
    </div>
  );
};

interface CreativeAllocationCardProps {
  company: string;
  color: string;
  isci: string;
  name: string;
  length: number;
  value: number;
  disabled?: boolean;
  disabledReason?: string;
  onChange: (isci: string, value: number) => void;
}

const CreativeAllocationCard = ({
  company,
  color,
  isci,
  name,
  length,
  value,
  disabled = false,
  disabledReason,
  onChange
}: CreativeAllocationCardProps) => (
  <DisabledReasonWrapper
    reason={disabled ? disabledReason : undefined}
    className='creative-allocation-card-tooltip'
  >
    <div className={cn('creative-allocation-card', { unallocated: value === 0, disabled })}>
      <div className='color-stripe' style={{ backgroundColor: color }} />
      <div className='card-body'>
        <div className='creative-img-box'>
          <Img
            src={getCreativeThumbnail(company, isci)}
            className='creative-img'
            unloader={<div className='no-img'>Thumbnail not found</div>}
          />
        </div>
        <div className='isci-label'>{isci}</div>
        <div className='creative-name'>{`${name} (${length}s)`}</div>
        <div className='allocation-input-group'>
          <Form.Control
            type='number'
            min={0}
            max={100}
            value={value}
            disabled={disabled}
            onChange={e => onChange(isci, Number(e.target.value))}
            className='allocation-input'
          />
          <span className='percent-suffix'>%</span>
        </div>
      </div>
    </div>
  </DisabledReasonWrapper>
);
